import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClockCounterClockwise, User, FileText } from "@phosphor-icons/react";

const ACTION_STYLE = {
  "shift.created":            { label: "Shift created",  tone: "text-[#0047AB] bg-blue-50 border-[#0047AB]" },
  "shift.updated":            { label: "Shift updated",  tone: "text-[#0047AB] bg-blue-50 border-[#0047AB]" },
  "shift.deleted":            { label: "Shift deleted",  tone: "text-[#FF2400] bg-red-50 border-[#FF2400]" },
  "shift.assigned":           { label: "Shift assigned", tone: "text-[#0047AB] bg-blue-50 border-[#0047AB]" },
  "attendance.override_upsert":  { label: "Attendance overridden", tone: "text-[#B45309] bg-amber-50 border-[#F59E0B]" },
  "attendance.override_deleted": { label: "Override cleared",      tone: "text-gray-600 bg-gray-50 border-gray-300" },
  "leave.approved": { label: "Leave approved", tone: "text-[#16A34A] bg-green-50 border-[#16A34A]" },
  "leave.rejected": { label: "Leave rejected", tone: "text-[#FF2400] bg-red-50 border-[#FF2400]" },
  "leave.hr_created": { label: "Leave added by HR", tone: "text-[#8B5CF6] bg-purple-50 border-[#8B5CF6]" },
};

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function AuditLogs() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");

  useEffect(() => {
    const params = {};
    if (action !== "all") params.action = action;
    api.get("/audit-logs", { params }).then((r) => setRows(r.data));
  }, [action]);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (r.actor_name || "").toLowerCase().includes(s) ||
      (r.target_name || "").toLowerCase().includes(s) ||
      JSON.stringify(r.details || {}).toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Compliance</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Audit Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every HR action — overrides, leave decisions, shifts — recorded with actor, target, and timestamp.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Search actor / target / details…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9 max-w-sm"
          data-testid="audit-search"
        />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-9 w-60" data-testid="audit-filter-action">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="attendance.override_upsert">Attendance override</SelectItem>
            <SelectItem value="attendance.override_deleted">Override cleared</SelectItem>
            <SelectItem value="leave.approved">Leave approved</SelectItem>
            <SelectItem value="leave.rejected">Leave rejected</SelectItem>
            <SelectItem value="leave.hr_created">Leave added by HR</SelectItem>
            <SelectItem value="shift.created">Shift created</SelectItem>
            <SelectItem value="shift.updated">Shift updated</SelectItem>
            <SelectItem value="shift.deleted">Shift deleted</SelectItem>
            <SelectItem value="shift.assigned">Shift assigned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/60">
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">When</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Action</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Actor</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Target</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Details</th>
              </tr>
            </thead>
            <tbody data-testid="audit-rows">
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-500 text-sm">No audit entries match your filter.</td></tr>
              )}
              {filtered.map((r) => {
                const style = ACTION_STYLE[r.action] || { label: r.action, tone: "text-gray-600 border-gray-300 bg-white" };
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap flex items-center gap-1">
                      <ClockCounterClockwise size={12} /> {fmtDt(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${style.tone}`}>
                        {style.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-800 text-sm">
                        <User size={12} className="text-gray-400" /> {r.actor_name || "—"}
                        {r.actor_role && <span className="text-[10px] uppercase tracking-wider text-gray-400 ml-1">({r.actor_role})</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.target_name ? (
                        <>
                          <span>{r.target_name}</span>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wider">{r.target_type}</p>
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs">{r.target_type} · {(r.target_id || "").slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 max-w-md">
                      <FileText size={10} className="inline mr-1 text-gray-400" />
                      <code className="font-mono text-[11px]">{JSON.stringify(r.details || {})}</code>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
